import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Box, Button, Paper } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { coverageCheckSchema } from '@app/shared';
import type { CoverageCheckDto, CoverageCheckInput } from '@app/shared';
import { useNotify } from '../../components/NotifyProvider';
import { PageHeader } from '../../components/PageHeader';
import { SelectInput, TextInput, applyServerErrors } from '../../components/form';
import { api } from '../../lib/api';
import { withRequiredMessages } from '../inventory/formUtils';
import { useCarriers } from '../plans/lookups';

/**
 * Carrier check service availability (PC816), the reference's "CSA CHECK": a carrier and a
 * ZIP code. The answer has two parts: what the carrier says, and whether the ZIP is open in
 * this company's own service area.
 */
export function CoverageCheckPage({
  program = 'nonLifeline',
}: { program?: 'lifeline' | 'nonLifeline' } = {}) {
  const notify = useNotify();
  const carriers = useCarriers();
  const { control, handleSubmit, setError } = useForm<CoverageCheckInput>({
    defaultValues: { carrierId: '', zip: '', program },
    resolver: withRequiredMessages<CoverageCheckInput>(
      zodResolver(coverageCheckSchema) as Resolver<CoverageCheckInput>,
      { carrierId: 'Select Carrier' },
    ),
  });
  const check = useMutation({
    mutationFn: (input: CoverageCheckInput) =>
      api.post<CoverageCheckDto>('/enrollments/coverage', input),
  });
  const submit = handleSubmit(async (values) => {
    try {
      await check.mutateAsync(values);
    } catch (err) {
      check.reset();
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  });
  const result = check.data;

  return (
    <Box>
      <PageHeader title="CSA CHECK" code="PC816" />
      <Paper
        variant="outlined"
        component="form"
        noValidate
        aria-label="CSA CHECK"
        onSubmit={(e) => void submit(e)}
        sx={{
          p: 2,
          mb: 1.5,
          maxWidth: 720,
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr auto' },
          alignItems: 'start',
        }}
      >
        <SelectInput
          control={control}
          name="carrierId"
          label="Carrier"
          required
          emptyLabel="Select"
          options={(carriers.data ?? [])
            .filter((c) => c.status === 'Active')
            .map((c) => ({ value: c.id, label: `${c.name} (${c.code})` }))}
          helperText={carriers.isError ? 'Could not load the carriers' : undefined}
        />
        <TextInput
          control={control}
          name="zip"
          label="Zip Code"
          required
          autoComplete="off"
          slotProps={{ htmlInput: { maxLength: 5, inputMode: 'numeric' } }}
        />
        <Button type="submit" variant="contained" disabled={check.isPending} sx={{ mt: 0.25 }}>
          Submit
        </Button>
      </Paper>
      {result ? (
        <Box aria-label="Result" role="region" sx={{ maxWidth: 720, display: 'grid', gap: 1 }}>
          <Alert severity={result.covered ? 'success' : 'warning'}>
            {result.carrier}, ZIP {result.zip}
            {result.city ? ` (${result.city}, ${result.state})` : ''}: {result.carrierMessage}
          </Alert>
          <Alert severity={result.serviceable ? 'success' : 'info'}>
            Service Area: {result.serviceAreaMessage}
          </Alert>
        </Box>
      ) : null}
    </Box>
  );
}
